// Thin wrapper over the PostHog snippet loaded in public/index.html.
//
// The snippet's array stub assigns window.posthog synchronously before React
// boots and queues capture/identify/reset until array.js arrives, so callers
// never need to wait for the SDK — but ad blockers can keep the stub from
// existing at all, so every call here is null-safe and swallow-on-error.
// Analytics must never break the dashboard.
//
// Local development (REACT_APP_WENV === 'local') suppresses capture so dev
// clicks stay out of product analytics; identify/reset still pass through so
// the local profile behaves like production when debugging identity flows.

const ph = () => (typeof window !== 'undefined' && window.posthog) || null;
const isLocal = () => process.env.REACT_APP_WENV === 'local';

/** Record a product event. Safe no-op when PostHog is absent or env is local. */
export function capture(event, properties = {}) {
    try {
        if (!event) return;
        if (isLocal()) {
            // eslint-disable-next-line no-console
            console.debug(`[analytics:local] ${event}`, properties);
            return;
        }
        ph()?.capture(event, properties);
    } catch { /* analytics must never throw into UI code */ }
}

/** Tie this browser session to the signed-in user (email is the distinct id). */
export function identify(email, properties = {}) {
    try {
        if (!email) return;
        ph()?.identify(email, { email, ...properties });
    } catch { /* ignore */ }
}

/** Detach the person on logout/session expiry so the next login isn't merged. */
export function reset() {
    try {
        ph()?.reset();
    } catch { /* ignore */ }
}

/**
 * Billing event name derived from the plan row, e.g. a $39 USD monthly plan →
 * `plan_39_usd_clicked` / `plan_39_usd_subscribed`. Plans are database rows,
 * not code, so the name is computed from whatever price shape the row carries
 * (major-unit `price`, or minor-unit `chargeMinorUnits` / agent-plan fees).
 */
export function planEventName(plan, action) {
    const major = Number.isFinite(Number(plan?.price)) && plan?.price !== null && plan?.price !== undefined
        ? Number(plan.price)
        : Number.isFinite(Number(plan?.chargeMinorUnits)) && plan?.chargeMinorUnits !== null && plan?.chargeMinorUnits !== undefined
            ? Number(plan.chargeMinorUnits) / 100
            : Number(plan?.fees?.platformFeeMinorUnits) > 0
                ? Number(plan.fees.platformFeeMinorUnits) / 100
                : 0;
    const currency = String(plan?.currency || 'usd').toLowerCase();
    return `plan_${Math.round(major)}_${currency}_${action}`;
}
